/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListWidgetsRequest,
  ListWidgetsSecurity,
} from "../models/operations/listwidgets.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildWidgetsQuery,
  prefetchWidgets,
  queryKeyWidgets,
  WidgetsQueryData,
} from "./widgets.core.js";
export {
  buildWidgetsQuery,
  prefetchWidgets,
  queryKeyWidgets,
  type WidgetsQueryData,
};

export type WidgetsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listWidgets widgets
 *
 * @remarks
 * List the project's widgets, most recently updated first. Each is validated as it is read, so a widget a catalog change broke says so.
 */
export function useWidgets(
  request?: ListWidgetsRequest | undefined,
  security?: ListWidgetsSecurity | undefined,
  options?: QueryHookOptions<WidgetsQueryData, WidgetsQueryError>,
): UseQueryResult<WidgetsQueryData, WidgetsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildWidgetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listWidgets widgets
 *
 * @remarks
 * List the project's widgets, most recently updated first. Each is validated as it is read, so a widget a catalog change broke says so.
 */
export function useWidgetsSuspense(
  request?: ListWidgetsRequest | undefined,
  security?: ListWidgetsSecurity | undefined,
  options?: SuspenseQueryHookOptions<WidgetsQueryData, WidgetsQueryError>,
): UseSuspenseQueryResult<WidgetsQueryData, WidgetsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildWidgetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setWidgetsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: WidgetsQueryData,
): WidgetsQueryData | undefined {
  const key = queryKeyWidgets(...queryKeyBase);

  return client.setQueryData<WidgetsQueryData>(key, data);
}

export function invalidateWidgets(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "widgets", "list", ...queryKeyBase],
  });
}

export function invalidateAllWidgets(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "widgets", "list"],
  });
}
